import { useEffect, useMemo, useRef, useState, type RefObject } from "react";
import { Flex, Spin, Typography, theme } from "../ui";
import { spacing } from "../layout/spacing";

export interface HtmlDocumentPreviewProps {
  html: string;
  title: string;
  scale: number;
  loading?: boolean;
  failed?: boolean;
  loadingText: string;
  errorText: string;
  frameRef?: RefObject<HTMLIFrameElement | null>;
  onReadyChange?: (ready: boolean) => void;
  keepDocumentColors?: boolean;
}

/** Displays server-generated HTML; transport, document content and actions belong to the caller. */
export function HtmlDocumentPreview({ html, title, scale, loading = false, failed = false,
  loadingText, errorText, frameRef: externalFrameRef, onReadyChange, keepDocumentColors = false }: HtmlDocumentPreviewProps) {
  const { token } = theme.useToken();
  const ownFrameRef = useRef<HTMLIFrameElement | null>(null);
  const frameRef = externalFrameRef ?? ownFrameRef;
  const [loaded, setLoaded] = useState<{ html: string; failed: boolean } | null>(null);
  // A refetch remounts the iframe even when the returned markup is identical.
  if ((loading || failed) && loaded !== null) setLoaded(null);
  const previewHtml = useMemo(() => {
    if (!html.trim()) return "";
    // Append after document styles: screen zoom must never alter print output.
    const surface = keepDocumentColors ? "" : `html, body { background: ${token.colorBgLayout}; }`;
    const css = `<style>@media screen { ${surface} body { zoom: ${scale}; } }</style>`;
    const headEnd = html.lastIndexOf("</head>");
    return headEnd < 0 ? `${css}${html}` : `${html.slice(0, headEnd)}${css}${html.slice(headEnd)}`;
  }, [html, keepDocumentColors, scale, token.colorBgLayout]);
  const loadFailed = failed || (!loading && !previewHtml) || (loaded?.html === previewHtml && loaded.failed);
  const ready = !loading && !loadFailed && loaded?.html === previewHtml;
  useEffect(() => {
    onReadyChange?.(ready);
    return () => onReadyChange?.(false);
  }, [onReadyChange, ready]);

  const finishLoad = async () => {
    const document = frameRef.current?.contentDocument;
    if (!document || !previewHtml) return;
    try {
      await document.fonts?.ready;
      await Promise.all(Array.from(document.images, image => image.complete
        ? Promise.resolve()
        : new Promise<void>(resolve => {
          image.addEventListener("load", () => resolve(), { once: true });
          image.addEventListener("error", () => resolve(), { once: true });
        })));
      if (frameRef.current?.contentDocument === document) {
        setLoaded({ html: previewHtml, failed: Array.from(document.images).some(image => image.naturalWidth === 0) });
      }
    } catch {
      if (frameRef.current?.contentDocument === document) setLoaded({ html: previewHtml, failed: true });
    }
  };

  return <div style={{ position: "relative", height: "100%", minHeight: 520 }} aria-busy={!ready && !loadFailed}>
    {!loading && !loadFailed && <iframe key={previewHtml} ref={frameRef} srcDoc={previewHtml} title={title}
      sandbox="allow-same-origin allow-modals" onLoad={() => { void finishLoad(); }}
      onError={() => setLoaded({ html: previewHtml, failed: true })}
      style={{ width: "100%", height: "100%", minHeight: 520, border: 0, display: "block", visibility: ready ? "visible" : "hidden" }} />}
    {!ready && <Flex align="center" justify="center" gap={spacing.elementGap}
      role={loadFailed ? "alert" : "status"}
      style={{ position: "absolute", inset: 0, background: token.colorBgLayout }}>
      {!loadFailed && <Spin />}
      <Typography.Text type="secondary">{loadFailed ? errorText : loadingText}</Typography.Text>
    </Flex>}
  </div>;
}
